import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Weekly revenue for the dashboard KPI tile.
 *
 * Computed via a focused, date-windowed query (last 14 days, joined to the
 * service price) instead of the full client-side appointments array. The full
 * array is loaded oldest-first and capped at ~1000 rows by PostgREST, so for
 * high-volume tenants it never contains recent appointments — which made the
 * revenue tile read 0 €. This query only ever returns the relevant ~2 weeks.
 */
export interface WeeklyRevenue {
  thisWeekRevenue: number;
  lastWeekRevenue: number;
}

export const useWeeklyRevenue = (tenantId: string | null) => {
  return useQuery({
    queryKey: ["weekly-revenue", tenantId],
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<WeeklyRevenue> => {
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const fourteenDaysAgo = new Date(now);
      fourteenDaysAgo.setDate(now.getDate() - 14);

      const { data, error } = await supabase
        .from("appointments")
        .select("end_time, status, appointment_types!inner(price)")
        .eq("tenant_id", tenantId as string)
        .gte("end_time", fourteenDaysAgo.toISOString())
        .lte("end_time", now.toISOString());

      if (error) throw error;

      let thisWeekRevenue = 0;
      let lastWeekRevenue = 0;

      for (const row of data ?? []) {
        if ((row.status || "").toLowerCase() === "cancelled") continue;
        // Supabase types the embedded relation as an array; price lives on it.
        const type = row.appointment_types as unknown as { price: number | null } | null;
        const price = Number(type?.price ?? 0);
        if (!price) continue;
        const end = new Date(row.end_time);
        if (end > sevenDaysAgo) {
          thisWeekRevenue += price;
        } else {
          lastWeekRevenue += price;
        }
      }

      return { thisWeekRevenue, lastWeekRevenue };
    },
  });
};
